import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../api/client';
import Toast from './Toast';
import './UsageDashboard.css';

function UsageDashboard({ user }) {
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  const { data: usage, isLoading } = useQuery({
    queryKey: ['usage'],
    queryFn: () => apiClient.get('/api/usage').then(r => r.data),
    refetchInterval: 60000,
    enabled: !isAdmin,
  });

  // Warning toast at 80% of cloud API spend cap
  useEffect(() => {
    if (!usage || isAdmin) return;

    const today = new Date().toISOString().split('T')[0];
    const warningKey = `usage_warning_shown_${today}`;
    if (sessionStorage.getItem(warningKey)) return;

    const spent = usage.api_spend_used_usd || 0;
    const cap = usage.api_spend_cap_usd;

    if (cap && spent >= cap * 0.8) {
      const pct = Math.round((spent / cap) * 100);
      setWarningMessage(`You've used ${pct}% of your $${cap.toFixed(2)} monthly AI budget.`);
      setShowWarning(true);
      sessionStorage.setItem(warningKey, 'true');
    }
  }, [usage, isAdmin]);

  if (isLoading && !isAdmin) {
    return (
      <div className="usage-section">
        <div className="usage-row">
          <span className="usage-row-label">Deck Uploads</span>
          <span className="usage-row-value" style={{ color: 'var(--text-subtle)' }}>Loading...</span>
        </div>
      </div>
    );
  }

  if (isAdmin) {
    return (
      <div className="usage-section">
        <div className="usage-row">
          <span className="usage-row-label">Deck Uploads</span>
          <span className="usage-row-value usage-unlimited-value">Unlimited</span>
        </div>
        <div className="usage-row">
          <span className="usage-row-label">AI Budget</span>
          <span className="usage-row-value usage-unlimited-value">Unlimited</span>
        </div>
      </div>
    );
  }

  const decksUsed = usage?.decks_uploaded_total || usage?.decks_uploaded_month || 0;
  const decksLimit = usage?.deck_limit || 15;
  const decksPercent = Math.min(100, (decksUsed / decksLimit) * 100);
  const decksWarning = decksPercent >= 90;
  const decksAtLimit = decksPercent >= 100;

  const spendUsed = Number(usage?.api_spend_used_usd || 0);
  const spendCap = Number(usage?.api_spend_cap_usd || 0.72);
  const spendPercent = spendCap > 0 ? Math.min(100, (spendUsed / spendCap) * 100) : 0;
  const spendWarning = spendPercent >= 80;
  const spendAtLimit = spendPercent >= 100;

  const fmtUsd = (n) => `$${Number(n || 0).toFixed(2)}`;

  return (
    <>
      <div className="usage-section">
        <div className="usage-row">
          <span className="usage-row-label">Deck Uploads</span>
          <span className={`usage-row-value ${decksWarning ? 'warning' : ''} ${decksAtLimit ? 'at-limit' : ''}`}>
            {decksUsed}/{decksLimit}
          </span>
        </div>
        <div className="usage-progress-track">
          <div
            className={`usage-progress-fill ${decksWarning ? 'warning' : ''} ${decksAtLimit ? 'at-limit' : ''}`}
            style={{ width: `${decksPercent}%` }}
          />
        </div>

        <div className="usage-row" style={{ marginTop: 'var(--space-4)' }}>
          <span className="usage-row-label">AI Budget</span>
          <span className={`usage-row-value ${spendWarning ? 'warning' : ''} ${spendAtLimit ? 'at-limit' : ''}`}>
            {fmtUsd(spendUsed)}/{fmtUsd(spendCap)}
          </span>
        </div>
        <div className="usage-progress-track">
          <div
            className={`usage-progress-fill ${spendWarning ? 'warning' : ''} ${spendAtLimit ? 'at-limit' : ''}`}
            style={{ width: `${spendPercent}%` }}
          />
        </div>
        <div className="usage-reset-hint">Resets monthly</div>
      </div>

      <Toast message={warningMessage} isVisible={showWarning} onClose={() => setShowWarning(false)} />
    </>
  );
}

export default UsageDashboard;
